import styles from "css/components/question-row.module.css"
import { useState, useEffect } from "react"
import { useUpdateQuestionResultMutation } from "util/rtk-api"
import { useStudentLessonResultContext } from "contexts/student-lesson-result-context"

interface QuestionRowProps {
    questionId: string;
    index: number;
    question: string;
    answer: string;
    correct: boolean;
    score?: number;           // AI partial score (0.0–1.0)
    manualScore?: number;     // Teacher override (0.0–1.0)
    manuallyGraded?: boolean;
    correctAnswer?: string;
    questionType?: string;
    studentName?: string;
    displayLabel?: string;
}

// Grade bands (GRADING-POLICY.md): 60% is the passing line.
//   75-100  proficient           green
//   60-74   passing, needs work  yellow
//   < 60    failing              red
// Was 80/40, which showed a failing 45% as yellow — implying it was acceptable.
const getScoreColorClass = (pct: number) => {
    if (pct >= 75) return styles.scoreBadgeGreen
    if (pct >= 60) return styles.scoreBadgeYellow
    return styles.scoreBadgeRed
}

const correctAnswerLabel = (questionType?: string) => {
    if (questionType === 'multiple-choice') return 'Correct option:'
    if (questionType === 'fill-blank') return 'Expected:'
    if (questionType === 'short-answer') return 'Model answer:'
    return 'Answer:'
}

export const QuestionRow = ({
    questionId, index, question, answer, correct,
    score, manualScore, manuallyGraded = false,
    correctAnswer, questionType, studentName, displayLabel,
}: QuestionRowProps) => {
    const { studentId, lessonId, classId } = useStudentLessonResultContext()
    const [updateQuestionResult] = useUpdateQuestionResultMutation()

    const effectivePct = Math.round(((manualScore ?? score ?? (correct ? 1 : 0)) * 100))
    const [inputValue, setInputValue] = useState(String(effectivePct))
    const [isEdited, setIsEdited] = useState(manuallyGraded)

    useEffect(() => {
        const newPct = Math.round(((manualScore ?? score ?? (correct ? 1 : 0)) * 100))
        setInputValue(String(newPct))
    }, [score, manualScore])

    const handleScoreSave = () => {
        const parsed = parseFloat(inputValue)
        if (isNaN(parsed) || parsed < 0 || parsed > 100) return
        const rounded = Math.round(parsed)
        setInputValue(String(rounded))
        setIsEdited(true)
        updateQuestionResult({ studentId, lessonId, questionId, score: rounded, classId })
    }

    const handleReset = () => {
        const aiPct = Math.round((score ?? (correct ? 1 : 0)) * 100)
        setInputValue(String(aiPct))
        setIsEdited(false)
        updateQuestionResult({ studentId, lessonId, questionId, score: aiPct, reset: true, classId })
    }

    const displayPct = isNaN(parseFloat(inputValue)) ? effectivePct : parseFloat(inputValue)

    return (
        <div className={`${styles.container} ${isEdited ? styles.manuallyGradedRow : ''}`}>
            <div className={styles.row}>
                <div className={`${styles.column} ${styles.leftJustify}`}>
                    <div className={`${styles.scoreBadge} ${getScoreColorClass(effectivePct)}`}>
                        {effectivePct}%
                    </div>
                </div>
                <div>{displayLabel ?? (index + 1)}</div>
                <div className={`${styles.column} ${styles.questionColumn}`}>
                    <div>
                        {studentName ? (
                            <>
                                <div className={styles.boldText}>{studentName}</div>
                                <div>{answer}</div>
                            </>
                        ) : (
                            <>
                                <div>{question}</div>
                                <div className={styles.boldText}>Student Answer:</div>
                                <div>{answer}</div>
                            </>
                        )}
                        {correctAnswer && (
                            <div className={styles.correctAnswerRow}>
                                <span className={styles.boldText}>{correctAnswerLabel(questionType)}</span> {correctAnswer}
                            </div>
                        )}
                    </div>
                </div>
                <div className={`${styles.column} ${styles.leftJustify}`}>
                    <span className={styles.scoreInputLabel}>Override score</span>
                    <div className={styles.scoreInputRow}>
                        <input
                            className={styles.scoreInput}
                            type="number"
                            min="0"
                            max="100"
                            value={inputValue}
                            onChange={e => setInputValue(e.target.value)}
                            onBlur={handleScoreSave}
                            onKeyDown={e => { if (e.key === 'Enter') handleScoreSave() }}
                        />
                        <span className={styles.scoreInputSuffix}>/ 100</span>
                    </div>
                    {isEdited && (
                        <div className={styles.editedRow}>
                            <span className={styles.editedBadge}>Edited</span>
                            <button className={styles.resetLink} onClick={handleReset}>Reset</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
